import React, { useState } from 'react';
import { Modal, Button, Input } from 'antd';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { postRequest } from '../../Utils/AppApi';
import { isNumber } from '../../Utils/Common';
import {
    PAN_VERIFICATION,
    POST_FUND_TRANSFER_ADD_CUSTOMER
} from '../../Utils/appConstants';

function PanVerification(props) {
    const history = useHistory();
    const { mobile, findCustomerInfo } = props;

    const [isLoading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [panNumber, setPanNumber] = useState('');
    const [aadharNumber, setAadharNumber] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [verifiedData, setVerifiedData] = useState(null);
    const [verificationError, setVerificationError] = useState('');

    /**
     * ---------------------------------------------------------
     * Validate masked Aadhaar against entered Aadhaar
     * ---------------------------------------------------------
     *
     * Supported examples:
     *
     * 24XXXXXXXX38
     * XXXX-XXXX-1234
     * XXXX XXXX 1234
     * 12XX56XXXX90
     * 12**56****90
     *
     * Only exposed digits are compared at their exact positions.
     */
    const validateMaskedAadhaar = (maskedAadhaar, actualAadhaar) => {
        if (!maskedAadhaar || !actualAadhaar) {
            return {
                valid: false,
                reason: 'invalid_format'
            };
        }

        // User Aadhaar must always be exactly 12 digits
        const normalizedActual = String(actualAadhaar)
            .replace(/\D/g, '');

        if (normalizedActual.length !== 12) {
            return {
                valid: false,
                reason: 'invalid_format'
            };
        }

        /**
         * Remove common formatting characters from API response.
         *
         * Example:
         * XXXX-XXXX-1234
         * becomes
         * XXXXXXXXXX1234
         */
        const normalizedMasked = String(maskedAadhaar)
            .replace(/[\s-]/g, '')
            .toUpperCase();

        /**
         * Masked Aadhaar should represent 12 positions.
         *
         * Example:
         * 24XXXXXXXX38
         * length = 12
         */
        if (normalizedMasked.length !== 12) {
            return {
                valid: false,
                reason: 'invalid_format'
            };
        }

        let exposedDigitCount = 0;

        for (let i = 0; i < 12; i++) {
            const maskedChar = normalizedMasked[i];
            const actualChar = normalizedActual[i];

            /**
             * X or * means the digit is masked.
             * Nothing needs to be compared at this position.
             */
            if (maskedChar === 'X' || maskedChar === '*') {
                continue;
            }

            /**
             * If API exposes a digit,
             * it must exactly match the entered Aadhaar.
             */
            if (/^\d$/.test(maskedChar)) {
                exposedDigitCount++;

                if (maskedChar !== actualChar) {
                    return {
                        valid: false,
                        reason: 'mismatch',
                        position: i
                    };
                }

                continue;
            }

            /**
             * Any unexpected character means
             * the API masking format is not supported.
             */
            return {
                valid: false,
                reason: 'invalid_format'
            };
        }

        /**
         * If there are no exposed digits,
         * we cannot actually verify a match.
         */
        if (exposedDigitCount === 0) {
            return {
                valid: false,
                reason: 'no_exposed_digits'
            };
        }

        return {
            valid: true,
            reason: 'matched',
            exposedDigitCount
        };
    };

    const handleVerify = async () => {
        // ---------------------------------------------------------
        // Validate PAN
        // ---------------------------------------------------------

        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

        const normalizedPan = String(panNumber || '')
            .trim()
            .toUpperCase();

        if (!normalizedPan || !panRegex.test(normalizedPan)) {
            toast.error(
                'Please enter a valid PAN number (e.g., ABCDE1234F)'
            );
            return;
        }

        // ---------------------------------------------------------
        // Validate Aadhaar
        // ---------------------------------------------------------

        const normalizedAadhar = String(aadharNumber || '')
            .replace(/\D/g, '');

        if (
            !normalizedAadhar ||
            normalizedAadhar.length !== 12 ||
            !/^\d{12}$/.test(normalizedAadhar)
        ) {
            toast.error(
                'Please enter a valid 12-digit Aadhaar number'
            );
            return;
        }

        setLoading(true);
        setVerificationError('');

        try {
            const payload = {
                pancard: normalizedPan,
                aadhar: normalizedAadhar,
                mobile: mobile,
            };

            const res = await postRequest(
                PAN_VERIFICATION,
                payload,
                history
            );

            if (res && res.success) {
                const data = res.data || {};

                // ---------------------------------------------------------
                // Check verification status
                // ---------------------------------------------------------

                if (data.status === 'VALID') {

                    // ---------------------------------------------------------
                    // Aadhaar matching check
                    // ---------------------------------------------------------

                    const maskedAadhar = String(
                        data.masked_aadhaar_number || ''
                    ).trim();

                    const aadhaarValidation = validateMaskedAadhaar(
                        maskedAadhar,
                        normalizedAadhar
                    );

                    // ---------------------------------------------------------
                    // Invalid API Aadhaar format
                    // ---------------------------------------------------------

                    if (
                        aadhaarValidation.reason === 'invalid_format'
                    ) {
                        const errorMessage =
                            'Unable to verify the Aadhaar number because the Aadhaar details received from the server are in an invalid format.';

                        setVerificationError(errorMessage);
                        toast.error('Invalid Aadhaar details received');

                        setIsVerified(false);
                        setVerifiedData(null);
                        setLoading(false);

                        return;
                    }

                    // ---------------------------------------------------------
                    // API returned fully masked Aadhaar
                    // ---------------------------------------------------------

                    if (
                        aadhaarValidation.reason === 'no_exposed_digits'
                    ) {
                        const errorMessage =
                            'Unable to verify the Aadhaar number because the server did not provide any Aadhaar digits for matching.';

                        setVerificationError(errorMessage);
                        toast.error(
                            'Unable to verify Aadhaar details'
                        );

                        setIsVerified(false);
                        setVerifiedData(null);
                        setLoading(false);

                        return;
                    }

                    // ---------------------------------------------------------
                    // Aadhaar mismatch
                    // ---------------------------------------------------------

                    if (!aadhaarValidation.valid) {

                        /**
                         * Extract exposed digits only for
                         * displaying a useful error message.
                         *
                         * Example:
                         * 24XXXXXXXX38
                         * becomes:
                         * 24******38
                         *
                         * We deliberately don't display
                         * the complete Aadhaar.
                         */
                        const normalizedMaskedForMessage =
                            maskedAadhar
                                .replace(/[\s-]/g, '')
                                .toUpperCase();

                        const maskedDisplay =
                            normalizedMaskedForMessage
                                .split('')
                                .map((char) =>
                                    /\d/.test(char)
                                        ? char
                                        : '*'
                                )
                                .join('');

                        const errorMessage =
                            `The Aadhaar number you entered does not match the Aadhaar linked to this PAN (${maskedDisplay}). Please enter the correct Aadhaar number.`;

                        setVerificationError(errorMessage);

                        toast.error(
                            'Aadhaar number mismatch'
                        );

                        setIsVerified(false);
                        setVerifiedData(null);
                        setLoading(false);

                        return;
                    }

                    // ---------------------------------------------------------
                    // All checks passed
                    // ---------------------------------------------------------

                    setVerifiedData(data);
                    setIsVerified(true);

                    toast.success(
                        'PAN & Aadhaar verified successfully!'
                    );

                    // Auto-submit KYC with verified data
                    await handleSubmitKyc(data);

                } else {

                    // ---------------------------------------------------------
                    // Invalid PAN / Aadhaar
                    // ---------------------------------------------------------

                    const errorMsg =
                        data.message ||
                        'Verification failed. Please check your PAN and Aadhaar details.';

                    setVerificationError(errorMsg);

                    toast.error(errorMsg);

                    setIsVerified(false);
                    setVerifiedData(null);
                }

            } else {

                toast.error(
                    res?.message ||
                    'Verification failed. Please check your details.'
                );
            }

        } catch (err) {

            console.error(
                'PAN verification error:',
                err
            );

            toast.error(
                'Verification failed. Please try again.'
            );

        } finally {

            setLoading(false);
        }
    };

    // ---------------------------------------------------------
    // Submit KYC to Add Customer API
    // ---------------------------------------------------------

    const handleSubmitKyc = async (data) => {

        setLoading(true);

        try {

            const addressFromApi =
                data.address || {};

            const payload = {

                customerMobile: mobile,

                customerName:
                    data.registered_name || '',

                customerDob:
                    data.date_of_birth || '',

                address:
                    addressFromApi.full_address || '',

                pincode:
                    addressFromApi.pincode || '',

                state:
                    addressFromApi.state || '',

                city:
                    addressFromApi.city || '',

                district:
                    addressFromApi.city || '',

                // Full Aadhaar entered by user.
                // It has already passed masked Aadhaar validation.
                aadharNumber:
                    aadharNumber,

                panNumber:
                    data.pan || panNumber,

                // Store complete verification response
                aadharNumberData:
                    data,

                apiName:
                    'FUND_TRANSFER',
            };

            const res = await postRequest(
                POST_FUND_TRANSFER_ADD_CUSTOMER,
                payload,
                history
            );

            if (res && res.success) {

                toast.success(
                    'KYC completed successfully!'
                );

                setIsModalOpen(false);

                if (findCustomerInfo) {
                    findCustomerInfo();
                }

            } else {

                toast.error(
                    res?.message ||
                    'KYC submission failed'
                );
            }

        } catch (err) {

            console.error(
                'KYC submission error:',
                err
            );

            toast.error(
                'KYC submission failed'
            );

        } finally {

            setLoading(false);
        }
    };

    // ---------------------------------------------------------
    // Cancel
    // ---------------------------------------------------------

    const handleCancel = () => {

        setIsModalOpen(false);

        if (findCustomerInfo) {
            findCustomerInfo();
        }
    };

    return (
        <Modal
            title="PAN & Aadhaar Verification"
            open={isModalOpen}
            onCancel={handleCancel}
            footer={null}
            width={520}
            maskClosable={false}
            destroyOnClose
        >
            <div className="row">

                <div className="col-md-12">

                    {/* ---------------------------------------------------
                        PAN
                    --------------------------------------------------- */}

                    <div className="form-group">

                        <label>
                            PAN Number{' '}
                            <span className="text-danger">
                                *
                            </span>
                        </label>

                        <Input
                            value={panNumber}
                            onChange={(e) =>
                                setPanNumber(
                                    e.target.value
                                        .toUpperCase()
                                        .replace(/[^A-Z0-9]/g, '')
                                )
                            }
                            maxLength={10}
                            placeholder="ABCDE1234F"
                            disabled={
                                isVerified ||
                                isLoading
                            }
                        />

                    </div>

                    {/* ---------------------------------------------------
                        Aadhaar
                    --------------------------------------------------- */}

                    <div className="form-group">

                        <label>
                            Aadhaar Number{' '}
                            <span className="text-danger">
                                *
                            </span>
                        </label>

                        <Input
                            value={aadharNumber}
                            onChange={(e) =>
                                setAadharNumber(
                                    e.target.value
                                        .replace(/\D/g, '')
                                )
                            }
                            maxLength={12}
                            placeholder="12-digit Aadhaar"
                            onKeyDown={isNumber}
                            disabled={
                                isVerified ||
                                isLoading
                            }
                        />

                    </div>

                    {/* ---------------------------------------------------
                        Verification Error
                    --------------------------------------------------- */}

                    {verificationError &&
                        !isVerified && (

                            <div className="alert alert-danger">

                                <strong>
                                    ❌ Verification failed:
                                </strong>{' '}

                                {verificationError}

                                <br />

                                <small>
                                    Please check your PAN and Aadhaar
                                    details and try again.
                                </small>

                            </div>
                        )}

                    {/* ---------------------------------------------------
                        Verification Success
                    --------------------------------------------------- */}

                    {isVerified && (

                        <div className="alert alert-success">

                            <strong>
                                ✅ Verified!
                            </strong>{' '}

                            Your PAN and Aadhaar have been
                            verified successfully.

                            {verifiedData && (

                                <div className="mt-2">

                                    <p>
                                        <strong>
                                            Name:
                                        </strong>{' '}
                                        {verifiedData.registered_name ||
                                            'N/A'}
                                    </p>

                                    <p>
                                        <strong>
                                            PAN:
                                        </strong>{' '}
                                        {verifiedData.pan ||
                                            panNumber}
                                    </p>

                                    <p>
                                        <strong>
                                            Aadhaar:
                                        </strong>{' '}
                                        {aadharNumber}
                                    </p>

                                    <p>
                                        <strong>
                                            DOB:
                                        </strong>{' '}
                                        {verifiedData.date_of_birth ||
                                            'N/A'}
                                    </p>

                                    <p>
                                        <strong>
                                            Address:
                                        </strong>{' '}
                                        {verifiedData.address
                                            ?.full_address ||
                                            'N/A'}
                                    </p>

                                </div>
                            )}

                        </div>
                    )}

                    {/* ---------------------------------------------------
                        Buttons
                    --------------------------------------------------- */}

                    <div className="text-right mt-3">

                        <Button
                            onClick={handleCancel}
                            className="mr-2"
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>

                        {!isVerified ? (

                            <Button
                                type="primary"
                                onClick={handleVerify}
                                loading={isLoading}
                            >
                                Verify & Submit
                            </Button>

                        ) : (

                            <Button
                                type="primary"
                                onClick={() =>
                                    handleSubmitKyc(
                                        verifiedData
                                    )
                                }
                                loading={isLoading}
                            >
                                Complete KYC
                            </Button>

                        )}

                    </div>

                </div>

            </div>
        </Modal>
    );
}

export default PanVerification;